import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const BASE = 'https://pdf-edit.tech';
const SITE_NAME = 'TG Edit-Pdf';

/**
 * Runtime head tags rendered on every page: html lang, canonical, Open Graph and
 * Twitter. Kept in sync with what scripts/inject-seo.js bakes into the prerendered
 * HTML, so the head Googlebot sees after rendering JS matches the static one.
 *
 * Deliberately NOT emitted here:
 * - JSON-LD. inject-seo.js writes the per-route @graph (Organization, WebSite,
 *   WebApplication/Article, BreadcrumbList, FAQPage, HowTo) into the prerendered
 *   HTML; emitting it again at runtime duplicated FAQPage/BreadcrumbList blocks,
 *   which Search Console reports as structured-data errors.
 * - hreflang. Long-form content (tool explainers, FAQs, blog, glossary) is
 *   English-only and only the UI chrome is translated, so ?lang=xx URLs were
 *   near-duplicates. The canonical is always the clean, query-free URL and the
 *   language switcher is a UI preference only.
 */
export default function StructuredData({ name, description, path = '', image }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const url = `${BASE}${path || '/'}`;
  const ogImage = image || `${BASE}/og-image.png`;

  return (
    <Helmet>
      <html lang={lang} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={name} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — free online PDF tools`} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={name} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
